/**
 * Slice 1 I3 / I4 / I5 — invite Create account · Sign in · Check email
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { mobileEmailInputProps, mobilePasswordInputProps } from "@/lib/mobileAuthInputs";

export type InviteAuthMode = "signup" | "login" | "check-email";

interface Props {
  mode: InviteAuthMode;
  onModeChange: (mode: InviteAuthMode) => void;
  email: string;
  onEmailChange: (v: string) => void;
  emailLocked?: boolean;
  password: string;
  onPasswordChange: (v: string) => void;
  firstName: string;
  onFirstNameChange: (v: string) => void;
  lastName: string;
  onLastNameChange: (v: string) => void;
  propertyName?: string | null;
  inviterName?: string | null;
  isSubmitting: boolean;
  isResettingPassword?: boolean;
  onSubmitSignIn: (e: React.FormEvent) => void;
  onSubmitSignUp: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
  onResend?: () => void;
}

export function InviteAuthScreens({
  mode,
  onModeChange,
  email,
  onEmailChange,
  emailLocked,
  password,
  onPasswordChange,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  propertyName,
  inviterName,
  isSubmitting,
  isResettingPassword,
  onSubmitSignIn,
  onSubmitSignUp,
  onForgotPassword,
  onResend,
}: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const sub = [propertyName, inviterName].filter(Boolean).join(" · ") || "Invite";

  if (mode === "check-email") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#f4f7f9] p-4">
        <div className="flex w-full max-w-[390px] flex-col items-center gap-[22px] py-16">
          <div className="flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#d7efec]">
            <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="2" strokeLinecap="round">
              <rect x="2.5" y="5" width="19" height="14" rx="3" />
              <path d="M3.5 7l8.5 6 8.5-6" />
            </svg>
          </div>
          <div className="text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">Check email</div>
          <div className="text-[17px] font-bold text-[#475569]">{email}</div>
          <Button
            type="button"
            variant="outline"
            className="h-14 rounded-[15px] border-[1.5px] border-[#d7e0e6] bg-white px-8 text-[17px] font-bold shadow-none hover:border-[#24a89a]"
            onClick={onResend}
            disabled={isSubmitting || isResettingPassword}
          >
            Resend
          </Button>
        </div>
      </div>
    );
  }

  const isSignUp = mode === "signup";

  return (
    <div className="flex min-h-[100dvh] justify-center bg-[#f4f7f9] p-4">
      <form
        onSubmit={isSignUp ? onSubmitSignUp : onSubmitSignIn}
        className="flex w-full max-w-[390px] flex-col gap-[22px] pt-10 pb-10"
        aria-label={isSignUp ? "Create account" : "Sign in"}
      >
        <div className="flex flex-col gap-1.5">
          <div className="text-[28px] font-bold tracking-[-0.5px] text-[#0f1720]">
            {isSignUp ? "Create account" : "Sign in"}
          </div>
          <div className="text-[15px] font-medium text-[#64748b]">{sub}</div>
        </div>

        <div className="flex flex-col gap-4">
          {isSignUp ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-[7px]">
                <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">First name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => onFirstNameChange(e.target.value)}
                  required
                  autoComplete="given-name"
                  placeholder="First"
                  className="h-[58px] rounded-[14px] border-[1.5px] border-[#24a89a] bg-white px-4 text-[18px] font-medium shadow-none focus-visible:ring-[#24a89a]"
                />
              </div>
              <div className="flex flex-col gap-[7px]">
                <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Last name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => onLastNameChange(e.target.value)}
                  required
                  autoComplete="family-name"
                  placeholder="Last"
                  className="h-[58px] rounded-[14px] border-[1.5px] border-[#24a89a] bg-white px-4 text-[18px] font-medium shadow-none focus-visible:ring-[#24a89a]"
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-[7px]">
            <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Email</Label>
            <div
              className={cn(
                "flex h-[58px] items-center gap-2.5 rounded-[14px] border-[1.5px] px-4 text-[18px] font-medium",
                emailLocked
                  ? "border-[#e6edf1] bg-[#eef2f5] text-[#475569]"
                  : "border-[#24a89a] bg-white text-[#0f1720]",
              )}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.2" strokeLinecap="round">
                <rect x="4" y="10.5" width="16" height="10.5" rx="2.5" />
                <path d="M8 10.5V7.5a4 4 0 018 0v3" />
              </svg>
              <Input
                {...mobileEmailInputProps}
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                onInput={(e) => onEmailChange((e.target as HTMLInputElement).value)}
                required
                readOnly={emailLocked}
                className="h-auto flex-1 border-0 bg-transparent p-0 text-[18px] font-medium shadow-none focus-visible:ring-0"
              />
            </div>
          </div>

          <div className="flex flex-col gap-[7px]">
            <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Password</Label>
            <div className="flex h-[58px] items-center justify-between rounded-[14px] border-[1.5px] border-[#24a89a] bg-white px-4">
              <Input
                {...mobilePasswordInputProps}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                onInput={(e) => onPasswordChange((e.target as HTMLInputElement).value)}
                required
                minLength={6}
                placeholder="••••••••"
                className="h-auto flex-1 border-0 bg-transparent p-0 text-[18px] font-medium tracking-wide shadow-none focus-visible:ring-0"
              />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="text-[#64748b]" aria-label="Toggle password">
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="h-[58px] rounded-[15px] bg-[#24a89a] text-[18px] font-bold text-white shadow-none hover:bg-[#1f958a]"
        >
          {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : isSignUp ? "Create account" : "Sign in"}
        </Button>

        {isSignUp ? (
          <button
            type="button"
            className="text-center text-[14px] font-medium text-[#64748b]"
            onClick={() => onModeChange("login")}
          >
            Sign in
          </button>
        ) : (
          <button
            type="button"
            className="text-center text-[14px] font-medium text-[#24a89a]"
            onClick={onForgotPassword}
            disabled={isResettingPassword}
          >
            {isResettingPassword ? "Sending..." : "Forgot password?"}
          </button>
        )}
      </form>
    </div>
  );
}
